import React from 'react'
import {LinkBox, Flex, Text, Skeleton, Stack, LinkOverlay, useStyleConfig} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {isHydrated} from '../../utils/utils'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {filterImageGroups, getPriceData} from '../../utils/product-utils'
import {PRODUCT_TRANSPARENT_BACKGROUND_IMAGE_TYPE} from '../../constants'
import {productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import PropTypes from 'prop-types'
import DynamicImage from '@app/components/dynamic-image'
import PDCTitle from '../pdc-title'
import DisplayPrice from '../display-price'
import useMultiSite from '../../hooks/use-multi-site'
import useShipToCountry from '../../hooks/use-ship-to-country'

const ProductDyptichTile = ({
    product,
    textColor,
    subTitle,
    titleId,
    desc,
    linkLabel,
    displayPriority
}) => {
    const {shipToCountry} = useShipToCountry()

    // Get the product data
    const {data: productData} = useProduct({
        parameters: {
            id: product,
            allImages: true,
            c_country: shipToCountry?.id
        }
    })

    // Get all image groups of the product.
    const filteredImageGroups = filterImageGroups(productData?.imageGroups, {
        viewType: PRODUCT_TRANSPARENT_BACKGROUND_IMAGE_TYPE
    })

    // Return the first image of the first group.
    const image = filteredImageGroups?.[0]?.images[0]

    // Primary URL used to wrap the component.
    const {buildUrl} = useMultiSite()
    const productUrl = buildUrl(productUrlBuilder({id: productData?.id}))

    // Get the right price for the product.
    const priceData = getPriceData(productData)

    // Get the currency
    const {currency} = useCurrency()

    const styles = useStyleConfig('Link', {})
    styles.textDecoration = 'none'
    styles._hover = {
        textDecoration: 'none'
    }

    return isHydrated() && productData ? (
        <LinkBox
            display={'flex'}
            flexDirection="column"
            justifyContent="center"
            alignItems={'center'}
            width={['100%', null, '50%']}
            pt={'20px'}
            pb={'40px'}
            px={['10px', null, '2%', '11.35%']}
            color={textColor || 'fullBlack'}
            minHeight={['158vw', null, 'auto']}
        >
            <Flex justifyContent={'center'} direction={'column'} height={['auto', null, '100%']}>
                {isHydrated() && image && (image?.disBaseLink || image?.link) ? (
                    <DynamicImage
                        data-testid="pdc-home-product-dyptich-video-image"
                        src={`${image?.disBaseLink || image?.link}[?sw={width}]`}
                        display="flex"
                        justifyContent={'center'}
                        widths={['56vw', '56vw', '28vw', '28vw', '28vw', '28vw']}
                        mx={['21.55%', null, null, 0]}
                        imageProps={{
                            width: '100%',
                            height: 'auto',
                            loading: displayPriority ? 'eager' : 'lazy',
                            fetchpriority: displayPriority ? 'high' : null,
                            aspectRatio: '4 / 5',
                            alt: image?.alt,
                            title: image?.title
                        }}
                    />
                ) : (
                    <Skeleton
                        aspectRatio={'4 / 5'}
                        height="auto"
                        width={'100%'}
                        mx={['21.55%', null, null, 0]}
                    />
                )}

                <Flex direction="column" alignItems="center" flex={0} mt={16}>
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: subTitle
                        }}
                        variant="descriptor"
                        textAlign={'center'}
                    />

                    <PDCTitle
                        width={['90%', null, '100%']}
                        title={productData?.name}
                        titleFontColor={textColor}
                        titleHeadingLevel={'h2'}
                        txtHorizontalPos={'center'}
                        textTransform="uppercase"
                        id={titleId}
                        mt={'10px'}
                        size="4xl"
                    />

                    <DisplayPrice
                        priceData={priceData}
                        currency={currency}
                        mt={'10px'}
                        textAlign={'center'}
                        display="flex"
                        currentPriceProps={{
                            variant: 'eyeBrowSmall'
                        }}
                        listPriceProps={{
                            variant: 'eyeBrowSmall'
                        }}
                    />
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: desc
                        }}
                        variant="bodyLarge2"
                        textAlign={'center'}
                        mt={'16px'}
                        px={[null, null, null, '40px']}
                        color={textColor || 'darkGray'}
                    />
                    <LinkOverlay as={RouteLink} to={productUrl} mt="10px" {...styles}>
                        <Text variant="link" as="span" color={textColor} textAlign={'center'}>
                            {linkLabel}
                        </Text>
                    </LinkOverlay>
                </Flex>
            </Flex>
        </LinkBox>
    ) : (
        <Flex
            width={['100%', null, '50%']}
            minHeight={['158vw', null, 'auto']}
            justifyContent="center"
            alignItems={'center'}
        >
            <Stack
                width={['75%', null, '55%']}
                gap={16}
                justifyContent="center"
                alignItems={'center'}
            >
                <Skeleton aspectRatio={'4 / 5'} width={['224px', null, '100%']} />
                <Stack gap={10} justifyContent="center" alignItems={'center'}>
                    <Skeleton height={'12px'} width={200} />
                    <Skeleton height={'64px'} width={250} />

                    <Skeleton height={'17px'} width={32} />
                </Stack>
                <Skeleton height={'36px'} width={313} />
                <Skeleton height={'17px'} width={110} />
            </Stack>
        </Flex>
    )
}

ProductDyptichTile.propTypes = {
    product: PropTypes.string.isRequired,
    subTitle: PropTypes.string,
    desc: PropTypes.string,
    linkLabel: PropTypes.string.isRequired,
    textColor: PropTypes.string,
    titleId: PropTypes.string,
    displayPriority: PropTypes.bool
}

export default ProductDyptichTile
